import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  DialBlockCampaignI,
  DialBlockCommitmentI,
  DialBlockI,
  DialBlockScoreResponseI,
} from "@/api/routes/dial-blocks";
import { useApiClient } from "@/context/api-client";
import type { APII } from "@/api/glencoco";

import { isDialBlocksSwitchedOff } from "@/modules/dial-blocks/lib/errors";
import { DialBlocksQueryKeys } from "./keys";

export interface MyWeekDataI {
  /** False while the backend switch is off (every route answers 404). */
  isSwitchedOn: boolean;
  /** The caller's live blocks, oldest first. */
  blocks: Array<DialBlockI>;
  /**
   * The campaigns the caller can book, with what is left of each that week
   * (B2b); undefined while the server doesn't send them yet.
   */
  campaigns?: Array<DialBlockCampaignI>;
  /** The caller's commitment for the week (B2c); undefined until sent. */
  commitment?: DialBlockCommitmentI;
}

const byStart = (a: DialBlockI, b: DialBlockI) =>
  Date.parse(a.start_time) - Date.parse(b.start_time);

/** One week's list; a switched-off backend is an answer, not an error. */
export const fetchMyWeek = async (
  api: APII,
  weekStart: string
): Promise<MyWeekDataI> => {
  try {
    const response = await api.dialBlocks.listWeek(weekStart);

    const { campaigns, commitment } = response.data ?? {};

    return {
      isSwitchedOn: true,
      blocks: [...(response.data?.dial_blocks ?? [])].sort(byStart),
      // Each piece shows only once its field arrives (B2b, B2c).
      campaigns: Array.isArray(campaigns) ? campaigns : undefined,
      commitment: commitment ?? undefined,
    };
  } catch (error) {
    if (isDialBlocksSwitchedOff(error)) {
      return { isSwitchedOn: false, blocks: [] };
    }

    throw error;
  }
};

/**
 * The caller's blocks in one ET week. Window focus refetches it, which is how
 * a change their LGA made shows up. The previous week stays on screen while
 * the next one loads.
 */
export const useFetchMyWeek = (weekStart: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: DialBlocksQueryKeys.week(weekStart),
    queryFn: () => fetchMyWeek(api, weekStart),
    enabled,
    placeholderData: keepPreviousData,
  });
};

export type DialBlockScoreDataI =
  | ({ isAvailable: true } & DialBlockScoreResponseI)
  | { isAvailable: false };

/**
 * The caller's dependability score (B6). A 404 with 1035 (switch off, or the
 * route not deployed yet) is an answer: the score isn't available.
 */
export const useFetchDialBlockScore = (enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: DialBlocksQueryKeys.score(),
    queryFn: async (): Promise<DialBlockScoreDataI> => {
      try {
        const response = await api.dialBlocks.getScore();

        return { isAvailable: true, ...response.data };
      } catch (error) {
        if (isDialBlocksSwitchedOff(error)) {
          return { isAvailable: false };
        }

        throw error;
      }
    },
    enabled,
  });
};

/**
 * Whether the backend's `dial_blocks_enabled` switch is on, asked once per
 * app load (the nav entry renders only on `true`). Any failure other than the
 * switch's 404 is an error, and the entry stays hidden: fail closed.
 */
export const useFetchDialBlocksSwitch = () => {
  const api = useApiClient();

  return useQuery({
    queryKey: DialBlocksQueryKeys.switch(),
    queryFn: async () => {
      try {
        await api.dialBlocks.listWeek();

        return true;
      } catch (error) {
        if (isDialBlocksSwitchedOff(error)) {
          return false;
        }

        throw error;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
